import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { toast } from "sonner"
import {
  getMissingPathologyCounts,
  getMissingPathologyTerms,
  getMissingPathologyLogsByTerm,
  getCanonicalNames,
  updatePathologyTermMapping,
  getMissingPathologyLogByLogId,
  getDistinctBodyParts,
} from "../api/missingPathologyLogs"

export const useMissingPathologyCounts = ({ filters = {} } = {}) =>
  useQuery({
    queryKey: ["missing-pathology-counts", filters],
    queryFn: () => getMissingPathologyCounts({ filters }),
    retry: false,
    select: (data) => data?.data,
  })

export const useMissingPathologyTermsInfinite = ({
  limit = 20,
  sortBy = "UpdatedAt:DESC",
  filters = {},
} = {}) =>
  useInfiniteQuery({
    queryKey: ["missing-pathology-terms-infinite", limit, sortBy, filters],
    queryFn: ({ pageParam = 1 }) =>
      getMissingPathologyTerms({ page: pageParam, limit, sortBy, filters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.page < lastPage?.pages ? lastPage.page + 1 : undefined,
    retry: false,
    enabled: !!filters.BodyPart && !!filters.status,
  })

export const useMissingPathologyLogsByTerm = ({
  pathologyTermMappingId,
  page = 1,
  limit = 20,
  sortBy = "createdAt:DESC",
  filters = {},
}) =>
  useQuery({
    queryKey: [
      "missing-pathology-logs-by-term",
      pathologyTermMappingId,
      page,
      limit,
      sortBy,
      filters,
    ],
    queryFn: () =>
      getMissingPathologyLogsByTerm({
        pathologyTermMappingId,
        page,
        limit,
        sortBy,
        filters,
      }),
    retry: false,
    enabled: !!pathologyTermMappingId,
    select: (data) => data,
  })

export const useCanonicalNames = ({ BodyPart, search, includeInactive } = {}) =>
  useQuery({
    queryKey: ["canonical-names", BodyPart, search, includeInactive],
    queryFn: () => getCanonicalNames({ BodyPart, search, includeInactive }),
    retry: false,
    enabled: !!BodyPart,
    select: (data) => data?.data,
  })

export const useUpdatePathologyTermMapping = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updatePathologyTermMapping,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["missing-pathology-counts"] })
      queryClient.invalidateQueries({
        queryKey: ["missing-pathology-terms-infinite"],
      })
      queryClient.invalidateQueries({
        queryKey: ["missing-pathology-logs-by-term"],
      })
      toast.success(response?.message || "Pathology term updated")
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update pathology term"
      toast.error(message)
    },
  })
}

export const useMissingPathologyLogByLogId = (missingPathologyLogId) =>
  useQuery({
    queryKey: ["missing-pathology-log", missingPathologyLogId],
    queryFn: () => getMissingPathologyLogByLogId(missingPathologyLogId),
    retry: false,
    enabled: !!missingPathologyLogId,
    select: (data) => data?.data,
  })

export const useDistinctBodyParts = () =>
  useQuery({
    queryKey: ["distinct-body-parts"],
    queryFn: () => getDistinctBodyParts(),
    retry: false,
  })
